import { Component, OnDestroy, effect, input, signal } from '@angular/core';
import { Subject, takeUntil, timer } from 'rxjs';

export interface TimerConfig {
  type: 'date' | 'time';
  expireAt: Date;
  onExpire?: () => void;
}

interface TimeValues {
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'app-timer',
  template: `
    @if (hasExpired()) {
      <p class="font-bold text-5xl md:text-3xl text-red-500">Time Out!</p>
    } @else {
      @if (timer().type === 'date') {
        <div class="flex gap-5 text-xl">
          @for (unit of getVisibleUnits(); track unit.key) {
            <div>
              <span class="countdown font-bold text-3xl">
                <span
                  [style]="'--value:' + unit.value + ';'"
                  [attr.aria-live]="'polite'"
                  [attr.aria-label]="unit.value">
                  {{ unit.value }}
                </span>
              </span>
              {{ unit.label }}
            </div>
          }
        </div>
      } @else {
        <span class="countdown font-bold text-3xl">
          @for (unit of getTimeUnits(); track unit.key; let last = $last) {
            <span
              [style]="'--value:' + unit.value + ';'"
              [attr.aria-live]="'polite'"
              [attr.aria-label]="unit.value">
              {{ unit.value }}
            </span>
            @if (!last) {
              :
            }
          }
        </span>
      }
    }
  `,
})
export class Timer implements OnDestroy {
  timer = input.required<TimerConfig>();
  timeValues = signal<TimeValues>({
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  hasExpired = signal<boolean>(false);

  private destroy$ = new Subject<void>();

  constructor() {
    effect(onCleanup => {
      const timerConfig = this.timer();
      this.hasExpired.set(false);

      const updateTime = () => {
        const now = new Date();
        const diffTime = timerConfig.expireAt.getTime() - now.getTime();

        if (diffTime <= 0) {
          this.hasExpired.set(true);
          this.timeValues.set({
            months: 0,
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
          });
          timerConfig.onExpire?.();
          return;
        }

        this.timeValues.set(this.calculateTimeValues(diffTime));
      };

      // Initial update
      updateTime();

      // Start the timer
      const subscription = timer(1000, 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.hasExpired()) {
            updateTime();
          }
        });

      // Cleanup function for the effect
      onCleanup(() => {
        subscription.unsubscribe();
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getVisibleUnits() {
    const values = this.timeValues();
    const units = [
      { key: 'months', value: values.months, label: 'months' },
      { key: 'days', value: values.days, label: 'days' },
      { key: 'hours', value: values.hours, label: 'hours' },
      { key: 'minutes', value: values.minutes, label: 'minutes left!' },
    ];

    // Show unit if it has a value OR if any larger unit has a value
    return units.filter((unit, index) => {
      return unit.value > 0 || units.slice(0, index).some(u => u.value > 0);
    });
  }

  getTimeUnits() {
    const values = this.timeValues();
    return [
      { key: 'hours', value: values.hours },
      { key: 'minutes', value: values.minutes },
      { key: 'seconds', value: values.seconds },
    ];
  }

  private calculateTimeValues(diffTime: number): TimeValues {
    const months = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30)); // Approximate months
    const days = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24)
    );
    const hours = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    return { months, days, hours, minutes, seconds };
  }
}
